<template>
  <div class="wide-attachments-container" v-if="wideItemAttachments && wideItemAttachments.length">
    <div class="wide-attachments label">Inline images</div>

    <div class="loaded-attachments cursor-pointer" v-for="attachment in wideItemAttachments" :key="attachment.id">
      <div class="attachment" @click="openAttachment(attachment)" ondragstart="return false;" ondrop="return false;">
        <img
          v-on:click.stop="openAttachment(attachment)"
          :src="apiUrl + '/Attachment/attachment/' + attachment.id"
          :data-src="apiUrl + '/Attachment/attachment/' + attachment.id"
          v-if="imageFileExtensions.indexOf(attachment.fileExtension) !== -1"
        />

        <div v-if="isLoading" class="spinner d-flex align-items-center justify-content-center p-5">
          <loading-spinner></loading-spinner>
        </div>

        <div
          class="update-attachment badgebutton active"
          v-b-tooltip:hover
          title="Display in list"
          v-on:click.stop="moveAttachmentToList(attachment)"
        >
          <i class="far fa-arrow-alt-down"></i>
        </div>

        <div
          class="delete-attachment badgebutton danger active"
          v-b-tooltip:hover
          title="Delete this attachment"
          v-on:click.stop="confirmDelete(attachment)"
        >
          <i class="far fa-trash"></i>
        </div>
      </div>
    </div>

    <a
      :id="hiddenId"
      @click="window_.open(blobAttachmentUrl, '_blank', 'location=yes,height=800,width=1200,scrollbars=yes,status=yes')"
    ></a>
  </div>
</template>

<script lang="ts">
  import Vue from 'vue';
  import Component from 'vue-class-component';
  import { Prop } from 'vue-property-decorator';
  import { AttachmentsModule, AttachmentsStore } from '@/store/attachments';
  import { GlobalHelper } from '@/helpers/global-helper';
  import {
    AttachmentDestination,
    AttachmentDto,
    BoardItemDto,
    DeleteAttachmentDto,
    UpdateAttachmentDto,
  } from '@/apiclient/apiclient_generated';
  import { API_URL } from '@/config';
  import lodash from 'lodash';
  import LoadingSpinner from '@/components/Misc/LoadingSpinner.vue';

  @Component({ components: { LoadingSpinner } })
  export default class WideAttachments extends Vue {
    @Prop() boardItem: BoardItemDto;

    apiUrl = API_URL;
    imageFileExtensions = ['.jpg', '.jpeg', '.png', '.bmp'];
    attachmentsStore: AttachmentsModule = AttachmentsStore;

    hiddenId = 'hiddenId';
    blobAttachmentUrl = '';
    window_ = window;

    isLoading = false;

    get wideItemAttachments() {
      const filtered = this.boardItem.attachments?.filter((t) => t.isWideBoardItemAttachment);

      return lodash.orderBy(filtered, 'createdDate');
    }

    async openAttachment(attachment) {
      this.isLoading = true;

      const fileData = await this.attachmentsStore.downloadAttachment(attachment.id);

      const file = new Blob([fileData.data], { type: 'image/jpg;base64' });
      const url = URL.createObjectURL(file);
      this.blobAttachmentUrl = url;

      this.isLoading = false;

      document.getElementById(this.hiddenId).click();
    }

    async moveAttachmentToList(attachment: AttachmentDto) {
      const dto = {
        id: attachment.id,
        boardItemId: this.boardItem.id,
        isWideBoardItemAttachment: false,
      } as UpdateAttachmentDto;

      await this.attachmentsStore.updateAttachment(dto);
    }

    confirmDelete(attachment: AttachmentDto) {
      this.$bvModal
        .msgBoxConfirm('Do you really want to delete the attachment ' + attachment.fileName, {
          title: 'Confirm deletion',
          size: 'sm',
          buttonSize: 'sm',
          okVariant: 'danger',
          okTitle: 'Delete attachment',
          cancelTitle: 'No',
          footerClass: 'p-2',
          hideHeaderClose: false,
          centered: false,
        })
        .then((value) => {
          if (value) {
            const params = {
              ownerId: this.boardItem.id,
              attachmentDestination: AttachmentDestination.WideBoardItem,
            } as DeleteAttachmentDto;

            this.isLoading = true;

            this.attachmentsStore
              .deleteAttachment({ id: attachment.id, body: params, destination: AttachmentDestination.WideBoardItem })
              .then(() => {
                const index = this.boardItem.attachments.findIndex((t) => t.id === attachment.id);

                if (index >= 0) {
                  this.boardItem.attachments.splice(index, 1);
                }
              })
              .finally(() => {
                this.isLoading = false;
              });
          }
        })
        .catch(() => {
          this.isLoading = false;
        });
    }
  }
</script>

<style scoped lang="scss">
  .wide-attachments-container {
    padding-top: 20px;

    .loaded-attachments {
      border: 2px dashed #e5e5e5;
      background: #eaeaea;
      min-height: 100px;

      .attachment {
        display: flex;
        position: relative;

        img {
          width: 100%;
          height: auto;
        }

        &:hover {
          .delete-attachment {
            visibility: visible;
            opacity: 1;
            transition: all 0.3s;
          }
        }

        &:hover {
          .update-attachment {
            visibility: visible;
            opacity: 1;
            transition: all 0.3s;
          }
        }

        .update-attachment {
          position: absolute;
          visibility: hidden;
          opacity: 0;
          font-size: 0.8rem;
          margin-left: auto;

          top: 4px;
          right: 34px;
        }

        .delete-attachment {
          position: absolute;
          visibility: hidden;
          opacity: 0;
          font-size: 0.8rem;
          margin-left: auto;

          top: 4px;
          right: 4px;
        }

        .spinner {
          z-index: 1000;
          position: absolute;

          top: 25%;
          left: 50%;
          transform: translate(-50%, -50%);
        }
      }
    }

    #hiddenId {
      display: none;
    }
  }
</style>
